/**
 * SiteLayout - Header y footer coherentes con BaseTemplate
 * Usado por /portfolio y otras páginas públicas
 * Mantiene consistencia visual con el resto del sitio
 */

import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BaseTemplateFooter } from "@/components/templates/BaseTemplateFooter";
import { usePortfolioSections } from "@/hooks/usePortfolioSections";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { hasHomeGalleryFromSiteSettings } from "@/lib/baseTemplateGalleryFromSiteSettings";
import {
  resolveBrandNameFromSiteSettings,
  resolveHeaderLinkFromSiteSettings,
  toCloudinaryLogoDelivery,
} from "@/lib/baseTemplateSiteContent";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { BRAND_SUPPORT_EMAIL, BRAND_AGE } from "@/lib/brandConfig";
import { baseTemplateDarkShell } from "@/lib/baseTemplateDarkShell";
import { cn } from "@/lib/utils";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

interface SiteLayoutProps {
  children: React.ReactNode;
  /** Clases extra en el root (ej. instagram-browser-container en /portfolio). */
  rootClassName?: string;
}

export function SiteLayout({ children, rootClassName }: SiteLayoutProps) {
  const { settings } = useSiteSettings();
  const { showPortfolioNavLink } = usePortfolioSections();
  const { t } = useTranslation();
  const { isBaseTemplate, language, country } = useTemplateSelection();
  const isDark = isBaseTemplate;
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const headerUrl = useMemo(
    () => resolveHeaderLinkFromSiteSettings(settings),
    [settings.headerUrl],
  );
  const brandName = useMemo(
    () => resolveBrandNameFromSiteSettings(settings),
    [settings.title],
  );
  const logoDisplaySrc = useMemo(
    () => toCloudinaryLogoDelivery(settings.logoUrl),
    [settings.logoUrl],
  );
  // Edad desde site-settings (respetada en footer si está completa)
  const brandAge = (settings.brandAge?.trim() || BRAND_AGE || "").trim();
  const showTallySection = settings.showTallySection !== false;
  const showSobreMiNav = settings.showSobreMi !== false;

  const navLinks = useMemo(() => {
    const showHomeGallery =
      (settings.showGallerySection ?? true) &&
      hasHomeGalleryFromSiteSettings({
        galleryImageUrls: settings.galleryImageUrls,
        alternativeGalleryEnabled: settings.alternativeGalleryEnabled,
        alternativeGalleryUrls: settings.alternativeGalleryUrls,
        logoUrl: settings.logoUrl,
      });
    const galleryHref = showHomeGallery
      ? "/#portfolio"
      : showPortfolioNavLink
        ? "/portfolio"
        : null;

    const links: { href: string; label: string }[] = [
      { href: "/", label: t("baseTemplate.nav.home") },
    ];
    if (galleryHref) {
      links.push({ href: galleryHref, label: t("baseTemplate.nav.gallery") });
    }
    if (showPortfolioNavLink && galleryHref !== "/portfolio") {
      links.push({ href: "/portfolio", label: t("baseTemplate.nav.portfolio") });
    }
    if (showTallySection) {
      links.push({ href: "/#contacto", label: t("baseTemplate.nav.contact") });
    }
    return links;
  }, [settings, showPortfolioNavLink, showTallySection, t]);

  const linkIsActive = (href: string) =>
    href === "/"
      ? location.pathname === "/"
      : location.pathname + (location.hash || "") === href;

  return (
    <div
      className={cn(
        "min-h-screen flex flex-col font-body antialiased",
        isDark ? "bg-black text-white" : "bg-white text-black",
        rootClassName,
      )}
    >
      {/* Header - mismo estilo que BaseTemplate / BaseTemplateBlack */}
      <header
        className={cn(
          isDark
            ? baseTemplateDarkShell.header
            : "sticky top-0 z-50 w-full backdrop-blur-md pt-[env(safe-area-inset-top,0px)] border-b border-black/10 bg-white/95",
        )}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12 sm:h-16 min-h-[44px]">
            <div className="flex-shrink-0">
              {headerUrl.startsWith("http") ? (
                <a
                  href={headerUrl}
                  className={cn(
                    isDark
                      ? `${baseTemplateDarkShell.logoLink} inline-flex items-center gap-2.5`
                      : "inline-flex items-center gap-2.5 text-lg sm:text-xl font-display font-medium whitespace-nowrap tracking-wider transition-opacity text-black hover:opacity-80",
                  )}
                  aria-label={brandName}
                >
                  {logoDisplaySrc ? (
                    <img
                      src={logoDisplaySrc}
                      alt={brandName}
                      className={cn(
                        "h-8 w-8 sm:h-9 sm:w-9 shrink-0 object-cover",
                        isDark ? "rounded-full ring-1 ring-white/20" : "rounded-full ring-1 ring-black/10",
                      )}
                      width={36}
                      height={36}
                      decoding="async"
                    />
                  ) : null}
                  <span>{brandName}</span>
                </a>
              ) : (
                <Link
                  to={headerUrl || "/"}
                  className={cn(
                    isDark
                      ? `${baseTemplateDarkShell.logoLink} inline-flex items-center gap-2.5`
                      : "inline-flex items-center gap-2.5 text-lg sm:text-xl font-display font-medium whitespace-nowrap tracking-wider transition-opacity text-black hover:opacity-80",
                  )}
                  aria-label={brandName}
                >
                  {logoDisplaySrc ? (
                    <img
                      src={logoDisplaySrc}
                      alt={brandName}
                      className="h-8 w-8 sm:h-9 sm:w-9 rounded-full object-cover ring-1 ring-black/10 shrink-0"
                      width={36}
                      height={36}
                      decoding="async"
                    />
                  ) : null}
                  <span>{brandName}</span>
                </Link>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 flex-shrink-0">
              <nav
                className="hidden md:flex space-x-5 sm:space-x-6 mr-2"
                aria-label="Main navigation"
              >
                {navLinks.map((link) => {
                  const isActive = linkIsActive(link.href);
                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      className={cn(
                        isDark
                          ? isActive
                            ? baseTemplateDarkShell.navDesktopLinkActive
                            : baseTemplateDarkShell.navDesktopLink
                          : isActive
                            ? "font-body text-black transition-colors duration-300 font-medium focus:outline-none rounded tracking-wide text-sm focus:ring-2 focus:ring-black/20"
                            : "font-body text-black/60 hover:text-black transition-colors duration-300 font-medium focus:outline-none rounded tracking-wide text-sm focus:ring-2 focus:ring-black/20",
                      )}
                    >
                      {link.label}
                    </Link>
                  );
                })}
              </nav>
              <LanguageSwitcher variant={isDark ? "dark" : "light"} />
              <div className="md:hidden">
                <button
                  type="button"
                  aria-label="Toggle navigation menu"
                  aria-expanded={mobileMenuOpen}
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className={cn(
                    isDark ? baseTemplateDarkShell.menuButton : "text-black/60 hover:text-black focus:outline-none focus:ring-2 focus:ring-black/20 rounded",
                  )}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div
            className={cn(
              isDark
                ? baseTemplateDarkShell.mobileMenuPanel
                : "md:hidden border-t border-black/10 bg-white",
            )}
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navLinks.map((link) => {
                const isActive = linkIsActive(link.href);
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      isDark
                        ? isActive
                          ? `${baseTemplateDarkShell.mobileNavLink} text-white`
                          : baseTemplateDarkShell.mobileNavLink
                        : "font-body block px-3 py-2 font-medium tracking-wide transition-colors duration-300 " +
                          (isActive ? "text-black" : "text-black/60 hover:text-black"),
                    )}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </header>

      {/* Main — mismo id que BaseTemplate / BaseTemplateBlack */}
      <main id="main-content" className="flex-1 relative">
        {children}
      </main>

      <BaseTemplateFooter
        variant={isDark ? "dark" : "light"}
        language={language}
        contactHref={showTallySection ? "#contacto" : `mailto:${BRAND_SUPPORT_EMAIL}`}
        analyticsTemplate={isDark ? "BaseTemplateBlack" : undefined}
        countryCode={country}
        brandName={brandName}
        footerTagline={settings.footerTagline}
        brandAge={brandAge}
        showPortfolioNavLink={showPortfolioNavLink}
        showSobreMiNavLink={showSobreMiNav}
      />
    </div>
  );
}
